import { FC, ReactNode } from "react";
import { useNavigate } from "react-router";

import { ActionBar, useModulePathResolver } from "capitalrx-shell";

import { Title, Subheading } from "capitalrx-components-2.0/lib/typography";
import { Panel, Button, ButtonProps } from "capitalrx-components-2.0/lib/general";
import { Stack, Shelf, Grid, Spacing } from "capitalrx-components-2.0/lib/layout";

export interface ButtonContent extends ButtonProps {
  title: string | ReactNode;
  path?: string;
}

interface Props {
  children: ReactNode;
  controlBar?: ReactNode;
  pageTitle: ReactNode;
  stackSpacing?: Spacing;
  buttonContent?: ButtonContent;
  showButton?: boolean;
  showButtonGroup?: boolean;
  buttonGroupContents?: Array<ButtonContent>;
  buttonClassName?: string;
  canView?: boolean;
  showBackButton?: boolean;
}

export const PageContent: FC<Props> = (props: Props) => {
  const navigate = useNavigate();
  const modulePath = useModulePathResolver();

  const {
    pageTitle,
    children,
    controlBar,
    stackSpacing = "medium",
    showButton = false,
    buttonContent = {} as ButtonContent,
    showButtonGroup = false,
    buttonGroupContents,
    buttonClassName = "",
    canView,
    showBackButton = false,
  } = props;

  const { title: buttonTitle = "", size, appearance = "secondary", path, onClick, ...restProps } = buttonContent;

  const handleButtonClick = (): void => {
    if (path) navigate(modulePath(path));
    if (onClick) onClick();
  };

  const button = (
    <Button appearance={appearance} className={buttonClassName} onClick={handleButtonClick} size={size} {...restProps}>
      {buttonTitle}
    </Button>
  );

  function renderButtonGroup(): ReactNode {
    return (
      <Shelf>
        {buttonGroupContents.map(({ title, path, onClick, ...restProps }, index) => {
          const handleGroupButtonClick = (): void => {
            if (path) navigate(modulePath(path));
            if (onClick) onClick();
          };

          return (
            <Button key={`${index}-${title}`} {...restProps} onClick={handleGroupButtonClick}>
              {title}
            </Button>
          );
        })}
      </Shelf>
    );
  }

  function renderPageContents(): ReactNode {
    if (canView) return children;
    return (
      <Panel>
        <Stack alignment="center">
          <Subheading>You do not have permission to view this page.</Subheading>
        </Stack>
      </Panel>
    );
  }

  return (
    <>
      <ActionBar>
        <div>
          {controlBar}
          {showBackButton && (
            <Button appearance="secondary" icon="arrow-left" onClick={() => navigate(-1)}>
              go back
            </Button>
          )}
        </div>
      </ActionBar>
      <Grid>
        <Stack spacing={stackSpacing}>
          <Shelf>
            <Title>{pageTitle}</Title>
            <Shelf.Spacer />
            {showButton && buttonContent && button}
            {showButtonGroup && buttonGroupContents.length && renderButtonGroup()}
          </Shelf>
          {renderPageContents()}
        </Stack>
      </Grid>
    </>
  );
};
